// 컨설팅 세션 관리 헬퍼 함수들
import { supabase } from '../lib/supabase';
import { getActiveSystemPrompt } from './systemPromptService';

/**
 * 새로운 컨설팅 세션 생성 (프롬프트 버전 포함)
 */
export const createConsultingSession = async (userId, sessionName, systemPromptId = null, consultantType = 'latte-manager') => {
  try {
    let promptId = systemPromptId;
    let promptData = null;
    
    // systemPromptId가 전달되지 않은 경우 기존 방식으로 활성 프롬프트 가져오기
    if (!systemPromptId) {
      promptData = await getActiveSystemPrompt(userId);
      promptId = promptData.id === 'fallback' ? null : promptData.id;
    }
    
    const { data, error } = await supabase
      .from('consulting_sessions')
      .insert([{
        user_id: userId,
        name: sessionName,
        system_prompt_id: promptId,
        status: 'active',
        current_step: '1',
        progress_percentage: 0,
        session_data: {
          promptVersion: promptData?.version || null,
          promptSource: promptData ? (promptData.isFromDB ? 'database' : 'fallback') : 'selected',
          consultantType: consultantType // session_data에만 저장 (참고용)
        }
      }])
      .select()
      .single();

    if (error) throw error;
    return data;

  } catch (error) {
    console.error('컨설팅 세션 생성 실패:', error);
    throw error;
  }
};

/**
 * 컨설팅 세션 업데이트 (진행 상황, 메시지 수 등)
 */
export const updateConsultingSession = async (sessionId, updates) => {
  try {
    const { data, error } = await supabase
      .from('consulting_sessions')
      .update({
        ...updates,
        last_activity_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    return data;

  } catch (error) {
    console.error('컨설팅 세션 업데이트 실패:', error);
    throw error;
  }
};

/**
 * 세션 완료 처리 (만족도 점수, 소요 시간 등 기록)
 */
export const completeConsultingSession = async (sessionId, sessionData = {}) => {
  try {
    const startedAt = sessionData.startedAt;
    const completedAt = new Date();
    const durationMinutes = startedAt ? 
      Math.round((completedAt - new Date(startedAt)) / (1000 * 60)) : null;

    const { data, error } = await supabase
      .from('consulting_sessions')
      .update({
        status: 'completed',
        completed_at: completedAt.toISOString(),
        session_duration_minutes: durationMinutes,
        completion_rate: 100,
        user_satisfaction_score: sessionData.satisfactionScore || null,
        total_messages_count: sessionData.messageCount || 0,
        last_activity_at: completedAt.toISOString(),
        updated_at: completedAt.toISOString()
      })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;

    console.log(`세션 완료: ${sessionId}, 소요시간: ${durationMinutes}분`);
    return data;

  } catch (error) {
    console.error('컨설팅 세션 완료 처리 실패:', error);
    throw error;
  }
};

/**
 * 세션 중단 처리
 */
export const abandonConsultingSession = async (sessionId, currentStep, messageCount = 0) => {
  try {
    const progressMap = {
      '1': 10, '2': 20, '3': 40, '4': 60, 
      '5': 80, '6': 90, '7': 95, 'FINAL': 100
    };

    const { data, error } = await supabase
      .from('consulting_sessions')
      .update({
        status: 'abandoned',
        completion_rate: progressMap[currentStep] || 0,
        total_messages_count: messageCount,
        last_activity_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    return data;

  } catch (error) {
    console.error('컨설팅 세션 중단 처리 실패:', error);
    throw error;
  }
};

/**
 * AI 로그 기록 (개선된 버전 - message_id 포함, 비동기 처리)
 * @param {string} sessionId - 세션 ID
 * @param {string} messageId - AI 메시지 ID (chat_messages 테이블의 ID)
 * @param {string} userMessage - 사용자 메시지
 * @param {object} aiResponse - AI 응답 객체
 * @param {number} processingTime - 처리 시간 (ms)
 * @param {object} promptData - 프롬프트 데이터
 * @param {string} currentStep - 현재 컨설팅 단계
 */
export const logAIInteractionAsync = (sessionId, messageId, userMessage, aiResponse, processingTime, promptData, currentStep) => {
  // 브라우저 환경에서 사용 가능한 setTimeout을 사용하여 비동기 처리
  setTimeout(async () => {
    try {
      // 🧹 로그 정리: 핵심 정보만 로깅
      //console.log('💾 AI 로그 저장:', { sessionId, step: currentStep });
      
      // API used 정보를 aiResponse에서 추출
      const apiUsed = aiResponse?._modelUsed || 'gemini-2.5-flash-latest';
      
      const logData = {
        session_id: sessionId,
        message_id: messageId, // 새로 추가된 컬럼
        log_type: 'response',
        user_message: userMessage,
        ai_response: aiResponse,
        processing_time_ms: processingTime,
        current_step: currentStep,
        api_used: apiUsed,
        system_prompt_id: promptData?.id || null,
        prompt_version: promptData?.version || 'unknown'
      };
      
      const { error } = await supabase
        .from('ai_logs')
        .insert([logData]);

      if (error) {
        console.error('❌ AI 로그 저장 실패:', error.message);
        // 재시도 로직 추가 (선택적)
        scheduleRetry({
          sessionId, messageId, userMessage, aiResponse, 
          processingTime, promptData, currentStep
        });
      } else {
        // 🧹 로그 정리: 성공 시에는 간단히 로깅
        //console.log('✅ AI 로그 저장 완료');
      }

    } catch (error) {
      console.error('❌ AI 로그 저장 예외:', error.message);
      // 로그 실패는 주요 플로우를 방해하지 않음
    }
  }, 0); // 즉시 실행하지만 비동기로
};

/**
 * AI 로그 재시도 스케줄링 (실패시 백그라운드에서 재시도)
 */
const scheduleRetry = (logData, retryCount = 0) => {
  const maxRetries = 3;
  const retryDelay = Math.pow(2, retryCount) * 1000; // 지수 백오프: 1s, 2s, 4s
  
  if (retryCount >= maxRetries) {
    console.error('❌ AI 로그 저장 최대 재시도 횟수 초과');
    return;
  }
  
  setTimeout(async () => {
    try {
      console.log(`🔄 AI 로그 재시도 ${retryCount + 1}/${maxRetries}`);
      
      const apiUsed = logData.aiResponse?._modelUsed || 'gemini-2.5-flash-latest';
      
      const { error } = await supabase
        .from('ai_logs')
        .insert([{
          session_id: logData.sessionId,
          message_id: logData.messageId,
          log_type: 'response',
          user_message: logData.userMessage,
          ai_response: logData.aiResponse,
          processing_time_ms: logData.processingTime,
          current_step: logData.currentStep,
          api_used: apiUsed,
          system_prompt_id: logData.promptData?.id || null,
          prompt_version: logData.promptData?.version || 'unknown'
        }]);

      if (error) {
        console.error(`❌ 재시도 ${retryCount + 1} 실패:`, error.message);
        scheduleRetry(logData, retryCount + 1);
      } else {
        console.log(`✅ 재시도 ${retryCount + 1} 성공`);
      }
      
    } catch (error) {
      console.error(`❌ 재시도 ${retryCount + 1} 예외:`, error.message);
      scheduleRetry(logData, retryCount + 1);
    }
  }, retryDelay);
};

/**
 * 기존 AI 로그 기록 함수 (하위 호환성 유지)
 * @deprecated 새로운 logAIInteractionAsync 사용 권장
 */
export const logAIInteraction = async (sessionId, userMessage, aiResponse, processingTime, promptData, currentStep) => {
  try {
    const apiUsed = aiResponse?._modelUsed || 'gemini-2.5-flash-latest';
    
    const { error } = await supabase
      .from('ai_logs')
      .insert([{
        session_id: sessionId,
        log_type: 'response',
        user_message: userMessage,
        ai_response: aiResponse,
        processing_time_ms: processingTime,
        current_step: currentStep,
        api_used: apiUsed,
        system_prompt_id: promptData?.id || null,
        prompt_version: promptData?.version || 'unknown'
      }]);

    if (error) throw error;

  } catch (error) {
    console.error('AI 로그 기록 실패:', error);
    // 로그 실패는 주요 플로우를 방해하지 않도록 에러를 던지지 않음
  }
};

/**
 * 사용자의 활성 세션 가져오기
 */
export const getUserActiveSession = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('consulting_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return data;

  } catch (error) {
    console.error('활성 세션 조회 실패:', error);
    return null;
  }
};

export default {
  createConsultingSession,
  updateConsultingSession,
  completeConsultingSession,
  abandonConsultingSession,
  logAIInteraction,
  logAIInteractionAsync,
  getUserActiveSession
};